import { Card, CardHeader } from '@/components/common';
import { formatCurrency, formatDate, titleCase } from '@/utils';

/**
 * Who or what a HEALTH or TRAVEL quote request covers (24 Sep).
 *
 * The public Health and Travel start forms store the visitor's answers in
 * `additional_data.health_data` (the broker wizard's own shape) and
 * `additional_data.travel_data`, and the detail page drew neither -- so an
 * advisor asked to "prepare quotes" could not see who to quote for. Read-only;
 * every field optional, since wizard rows, public rows and older rows carry
 * different subsets.
 */

interface HealthMember {
  relation?: string;
  age?: number | null;
  date_of_birth?: string | null;
}

export interface QuoteHealthData {
  members?: HealthMember[];
  adults?: number;
  children?: number;
  sum_insured?: string | number | null;
  pincode?: string;
  pre_existing_condition?: boolean;
  existing_policy?: { insurer_name?: string; expiry?: string | null } | null;
}

export interface QuoteTravelData {
  trip_type?: string;
  region_label?: string;
  region?: string;
  countries?: string;
  start_date?: string;
  end_date?: string;
  trip_days?: number;
  purpose?: string;
  traveller_ages?: number[];
}

function Item({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

const relationLabel = (relation?: string) => titleCase((relation || 'member').replace(/_/g, ' '));

export function QuoteCoverDetailsCard({
  health,
  travel,
}: {
  health?: QuoteHealthData | null;
  travel?: QuoteTravelData | null;
}) {
  if (health && typeof health === 'object') {
    const members = Array.isArray(health.members) ? health.members : [];
    const sumInsured = Number(health.sum_insured);
    return (
      <Card>
        <CardHeader title="Who to cover" subtitle="The health plan this request asks you to quote." />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Item
            label="Members"
            value={
              members.length > 0
                ? members
                    .map((m) => `${relationLabel(m.relation)}${m.age != null ? ` (${m.age})` : ''}`)
                    .join(', ')
                : '-'
            }
          />
          <Item
            label="Sum Insured"
            value={Number.isFinite(sumInsured) && sumInsured > 0 ? formatCurrency(sumInsured) : '-'}
          />
          <Item
            label="Pre-existing Illness"
            value={health.pre_existing_condition === undefined ? '-' : health.pre_existing_condition ? 'Yes' : 'No'}
          />
          <Item label="Pincode" value={health.pincode || '-'} />
          {health.existing_policy && (
            <Item
              label="Existing Policy"
              value={
                [
                  health.existing_policy.insurer_name,
                  health.existing_policy.expiry ? `expires ${formatDate(health.existing_policy.expiry)}` : '',
                ]
                  .filter(Boolean)
                  .join(', ') || '-'
              }
            />
          )}
        </div>
      </Card>
    );
  }

  if (travel && typeof travel === 'object') {
    const ages = Array.isArray(travel.traveller_ages) ? travel.traveller_ages : [];
    return (
      <Card>
        <CardHeader title="The trip" subtitle="The travel cover this request asks you to quote." />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Item
            label="Trip Type"
            value={travel.trip_type === 'annual_multi_trip' ? 'Annual multi-trip' : travel.trip_type ? 'Single trip' : '-'}
          />
          <Item
            label="Destination"
            value={[travel.region_label || travel.region, travel.countries].filter(Boolean).join(' -- ') || '-'}
          />
          <Item
            label="Dates"
            value={
              travel.start_date
                ? `${formatDate(travel.start_date)}${travel.end_date ? ` to ${formatDate(travel.end_date)}` : ''}${
                    travel.trip_days ? ` (${travel.trip_days} days)` : ''
                  }`
                : '-'
            }
          />
          <Item label="Purpose" value={travel.purpose ? titleCase(travel.purpose) : '-'} />
          <Item label="Traveller Ages" value={ages.length > 0 ? ages.join(', ') : '-'} />
        </div>
      </Card>
    );
  }

  return null;
}
